import {
  type CampaignEngagementEvent,
  type CampaignSendTimestamp,
  type CampaignTrendSeries,
} from './campaignEngagementTrend';
import { supabase } from './supabase';

export type EngagementSource = 'live' | 'rollup' | 'none';

export interface CampaignEngagementStats {
  source: EngagementSource;
  recipientsSent: number;
  uniqueOpens: number;
  uniqueClickers: number;
  totalClicks: number;
  linkClicks: { url: string; uniqueClickers: number; totalClicks: number }[];
  /** Latest Postmark ReceivedAt for an open on this campaign/step. */
  lastOpenAt: string | null;
  /** Latest Postmark ReceivedAt for a click on this campaign/step. */
  lastClickAt: string | null;
  rolledUpAt: string | null;
}

/** Parse timestamps from Postmark / Postgres (ISO or "YYYY-MM-DD HH:mm:ss+00"). */
export function parseEngagementTimestamp(value: string): Date | null {
  if (!value?.trim()) return null;
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format open/click time; pass automation TZ to match step schedule display. */
export function formatEngagementWhen(value: string, timeZone?: string): string {
  const d = parseEngagementTimestamp(value);
  if (!d) return value;
  return d.toLocaleString(undefined, {
    timeZone: timeZone?.trim() || undefined,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** @deprecated Use CampaignEngagementStats */
export type NewsletterEngagementStats = CampaignEngagementStats;

export interface SendEngagementFlag {
  opened: boolean;
  clicked: boolean;
}

interface EngagementEventRow {
  scheduled_email_id: string | null;
  event_type: string;
  destination_url: string | null;
  occurred_at?: string;
}

function aggregateEvents(events: EngagementEventRow[]) {
  const openSendIds = new Set<string>();
  const clickerSendIds = new Set<string>();
  let totalClicks = 0;
  let lastOpenAt: string | null = null;
  let lastClickAt: string | null = null;
  const perLink = new Map<string, { clickers: Set<string>; totalClicks: number }>();

  for (const e of events) {
    const sid = e.scheduled_email_id;
    if (!sid) continue;
    if (e.event_type === 'open') {
      openSendIds.add(sid);
      if (e.occurred_at && (!lastOpenAt || e.occurred_at > lastOpenAt)) {
        lastOpenAt = e.occurred_at;
      }
    } else if (e.event_type === 'click') {
      clickerSendIds.add(sid);
      totalClicks += 1;
      if (e.occurred_at && (!lastClickAt || e.occurred_at > lastClickAt)) {
        lastClickAt = e.occurred_at;
      }
      const url = e.destination_url?.trim() || '(unknown link)';
      if (!perLink.has(url)) perLink.set(url, { clickers: new Set(), totalClicks: 0 });
      const bucket = perLink.get(url)!;
      bucket.clickers.add(sid);
      bucket.totalClicks += 1;
    }
  }

  const linkClicks = [...perLink.entries()]
    .map(([url, b]) => ({
      url,
      uniqueClickers: b.clickers.size,
      totalClicks: b.totalClicks,
    }))
    .sort((a, b) => b.totalClicks - a.totalClicks);

  return {
    uniqueOpens: openSendIds.size,
    uniqueClickers: clickerSendIds.size,
    totalClicks,
    linkClicks,
    lastOpenAt,
    lastClickAt,
  };
}

export function engagementRate(numerator: number, denominator: number): string {
  if (denominator <= 0) return '—';
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

async function countStepRecipientsSent(automationStepId: string): Promise<number> {
  const { count, error } = await supabase
    .from('scheduled_emails')
    .select('id', { count: 'exact', head: true })
    .eq('automation_step_id', automationStepId)
    .eq('status', 'sent');
  if (error) throw new Error(error.message);
  return count ?? 0;
}

function emptyEngagementStats(recipientsSent: number): CampaignEngagementStats {
  return {
    source: 'none',
    recipientsSent,
    uniqueOpens: 0,
    uniqueClickers: 0,
    totalClicks: 0,
    linkClicks: [],
    lastOpenAt: null,
    lastClickAt: null,
    rolledUpAt: null,
  };
}

async function loadEngagementByFilter(
  filter: { newsletterId: string } | { automationStepId: string },
  recipientsSent: number,
): Promise<CampaignEngagementStats> {
  const empty = emptyEngagementStats(recipientsSent);

  let eventsQuery = supabase
    .from('email_engagement_events')
    .select(
      'scheduled_email_id, event_type, destination_url, occurred_at, scheduled_emails!inner(newsletter_id, automation_step_id)',
    );
  if ('newsletterId' in filter) {
    eventsQuery = eventsQuery.eq('scheduled_emails.newsletter_id', filter.newsletterId);
  } else {
    eventsQuery = eventsQuery.eq(
      'scheduled_emails.automation_step_id',
      filter.automationStepId,
    );
  }
  const { data: events, error: evErr } = await eventsQuery;
  if (evErr) throw new Error(evErr.message);

  const eventRows = (events ?? []) as EngagementEventRow[];
  if (eventRows.length > 0) {
    const agg = aggregateEvents(eventRows);
    return {
      source: 'live',
      recipientsSent,
      uniqueOpens: agg.uniqueOpens,
      uniqueClickers: agg.uniqueClickers,
      totalClicks: agg.totalClicks,
      linkClicks: agg.linkClicks,
      lastOpenAt: agg.lastOpenAt,
      lastClickAt: agg.lastClickAt,
      rolledUpAt: null,
    };
  }

  let rollupsQuery = supabase
    .from('email_engagement_rollups')
    .select(
      'destination_url, recipients_sent, unique_opens, unique_clickers, total_clicks, rolled_up_at',
    )
    .order('rolled_up_at', { ascending: false });
  if ('newsletterId' in filter) {
    rollupsQuery = rollupsQuery.eq('newsletter_id', filter.newsletterId);
  } else {
    rollupsQuery = rollupsQuery.eq('automation_step_id', filter.automationStepId);
  }
  const { data: rollups, error: ruErr } = await rollupsQuery;
  if (ruErr) throw new Error(ruErr.message);

  const rows = rollups ?? [];
  if (rows.length === 0) return empty;

  const totalRow = rows.find((r) => r.destination_url == null || r.destination_url === '');
  const linkRows = rows.filter((r) => r.destination_url != null && r.destination_url !== '');

  const recipients = totalRow?.recipients_sent ?? recipientsSent;
  return {
    source: 'rollup',
    recipientsSent: recipients,
    uniqueOpens: totalRow?.unique_opens ?? 0,
    uniqueClickers: totalRow?.unique_clickers ?? 0,
    totalClicks: totalRow?.total_clicks ?? 0,
    linkClicks: linkRows.map((r) => ({
      url: r.destination_url as string,
      uniqueClickers: r.unique_clickers ?? 0,
      totalClicks: r.total_clicks ?? 0,
    })),
    lastOpenAt: null,
    lastClickAt: null,
    rolledUpAt: totalRow?.rolled_up_at ?? rows[0]?.rolled_up_at ?? null,
  };
}

/** Campaign-level opens/clicks for a newsletter send. */
export async function loadNewsletterEngagement(
  newsletterId: string,
  recipientsSent: number,
): Promise<CampaignEngagementStats> {
  return loadEngagementByFilter({ newsletterId }, recipientsSent);
}

/** Opens/clicks for one automation step (all enrollments combined). */
export async function loadAutomationStepEngagement(
  automationStepId: string,
  recipientsSent?: number,
): Promise<CampaignEngagementStats> {
  const sent =
    recipientsSent ?? (await countStepRecipientsSent(automationStepId));
  return loadEngagementByFilter({ automationStepId }, sent);
}

/** Load sent counts + engagement for each step in parallel. */
export async function loadAutomationStepsEngagement(
  stepIds: string[],
): Promise<Map<string, CampaignEngagementStats>> {
  const entries = await Promise.all(
    stepIds.map(async (stepId) => {
      const sent = await countStepRecipientsSent(stepId);
      const stats = await loadAutomationStepEngagement(stepId, sent);
      return [stepId, stats] as const;
    }),
  );
  return new Map(entries);
}

/** Per scheduled_email_id flags for recipient table badges. */
export async function loadSendEngagementFlags(
  newsletterId: string,
): Promise<Map<string, SendEngagementFlag>> {
  const { data, error } = await supabase
    .from('email_engagement_events')
    .select('scheduled_email_id, event_type, scheduled_emails!inner(newsletter_id)')
    .eq('scheduled_emails.newsletter_id', newsletterId)
    .not('scheduled_email_id', 'is', null);
  if (error) throw new Error(error.message);

  const map = new Map<string, SendEngagementFlag>();
  for (const row of data ?? []) {
    const id = row.scheduled_email_id as string;
    let entry = map.get(id);
    if (!entry) {
      entry = { opened: false, clicked: false };
      map.set(id, entry);
    }
    if (row.event_type === 'open') entry.opened = true;
    if (row.event_type === 'click') entry.clicked = true;
  }
  return map;
}

export interface SubscriberEngagementRow {
  id: string;
  event_type: 'open' | 'click';
  occurred_at: string;
  destination_url: string | null;
  scheduled_email_id: string | null;
  scheduled_emails: {
    subject: string | null;
    newsletter_id: string | null;
    newsletters: { name: string | null } | null;
    automation_steps: { name: string | null } | null;
  } | null;
}

export async function loadSubscriberEngagement(
  subscriberId: string,
  limit = 50,
): Promise<SubscriberEngagementRow[]> {
  const { data, error } = await supabase
    .from('email_engagement_events')
    .select(
      `id, event_type, occurred_at, destination_url, scheduled_email_id,
       scheduled_emails!inner(subject, newsletter_id, subscriber_id,
         newsletters(name), automation_steps(name))`,
    )
    .eq('scheduled_emails.subscriber_id', subscriberId)
    .order('occurred_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as SubscriberEngagementRow[];
}

export interface NewsletterEngagementEventRow {
  id: string;
  event_type: 'open' | 'click';
  occurred_at: string;
  destination_url: string | null;
  scheduled_email_id: string | null;
  scheduled_emails: {
    id: string;
    subject: string | null;
    subscriber_id: string | null;
    subscribers: { email: string | null; name: string | null } | null;
  } | null;
}

/** Postmark open/click rows for one newsletter (newest first). */
export async function loadNewsletterEngagementEvents(
  newsletterId: string,
  limit = 200,
): Promise<NewsletterEngagementEventRow[]> {
  const { data, error } = await supabase
    .from('email_engagement_events')
    .select(
      `id, event_type, occurred_at, destination_url, scheduled_email_id,
       scheduled_emails!inner(id, subject, subscriber_id, newsletter_id,
         subscribers(email, name))`,
    )
    .eq('scheduled_emails.newsletter_id', newsletterId)
    .order('occurred_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as NewsletterEngagementEventRow[];
}

export type NewsletterEngagementTrendEvent = CampaignEngagementEvent;

export type NewsletterEngagementPanelData = {
  stats: CampaignEngagementStats;
  anchorAt: string | null;
  sends: CampaignSendTimestamp[];
  events: NewsletterEngagementTrendEvent[];
  hasLiveTrend: boolean;
};

/** Earliest sent_at for this newsletter, else planned send time. */
export async function loadNewsletterEngagementAnchor(
  newsletterId: string,
  scheduledFor?: string | null,
): Promise<string | null> {
  const { data, error } = await supabase
    .from('scheduled_emails')
    .select('sent_at')
    .eq('newsletter_id', newsletterId)
    .eq('status', 'sent')
    .not('sent_at', 'is', null)
    .order('sent_at', { ascending: true })
    .limit(1);
  if (error) throw new Error(error.message);

  const firstSent = data?.[0]?.sent_at as string | undefined;
  if (firstSent) return firstSent;

  const planned = scheduledFor?.trim();
  return planned || null;
}

/** All open/click events for trend charting (live window only). */
export async function loadNewsletterEngagementTrendEvents(
  newsletterId: string,
): Promise<NewsletterEngagementTrendEvent[]> {
  const { data, error } = await supabase
    .from('email_engagement_events')
    .select(
      'event_type, occurred_at, scheduled_email_id, scheduled_emails!inner(newsletter_id)',
    )
    .eq('scheduled_emails.newsletter_id', newsletterId)
    .order('occurred_at', { ascending: true })
    .limit(20000);
  if (error) throw new Error(error.message);
  return (data ?? []) as NewsletterEngagementTrendEvent[];
}

/** sent_at timestamps for cumulative send trend. */
export async function loadNewsletterSendTimestamps(
  newsletterId: string,
): Promise<CampaignSendTimestamp[]> {
  const { data, error } = await supabase
    .from('scheduled_emails')
    .select('sent_at')
    .eq('newsletter_id', newsletterId)
    .eq('status', 'sent')
    .not('sent_at', 'is', null)
    .order('sent_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as CampaignSendTimestamp[];
}

export type LinkClickersFilter =
  | { newsletterId: string }
  | { automationStepId: string };

export interface LinkClickerRow {
  subscriberId: string | null;
  email: string | null;
  name: string | null;
  clickCount: number;
  lastClickedAt: string;
}

interface LinkClickerEventRow {
  occurred_at: string;
  scheduled_email_id: string | null;
  scheduled_emails: {
    subscriber_id: string | null;
    subscribers: { email: string | null; name: string | null } | null;
  } | null;
}

function aggregateLinkClickers(events: LinkClickerEventRow[]): LinkClickerRow[] {
  const byKey = new Map<string, LinkClickerRow>();

  for (const e of events) {
    const subscriberId = e.scheduled_emails?.subscriber_id ?? null;
    const key = subscriberId ?? e.scheduled_email_id ?? e.occurred_at;
    const sub = e.scheduled_emails?.subscribers;

    let row = byKey.get(key);
    if (!row) {
      row = {
        subscriberId,
        email: sub?.email ?? null,
        name: sub?.name ?? null,
        clickCount: 0,
        lastClickedAt: e.occurred_at,
      };
      byKey.set(key, row);
    }
    row.clickCount += 1;
    if (e.occurred_at > row.lastClickedAt) {
      row.lastClickedAt = e.occurred_at;
    }
  }

  return [...byKey.values()].sort((a, b) => b.lastClickedAt.localeCompare(a.lastClickedAt));
}

/** People who clicked a specific link on a newsletter or automation step. */
export async function loadLinkClickers(
  filter: LinkClickersFilter,
  destinationUrl: string,
): Promise<LinkClickerRow[]> {
  let query = supabase
    .from('email_engagement_events')
    .select(
      `occurred_at, scheduled_email_id,
       scheduled_emails!inner(subscriber_id,
         subscribers(email, name))`,
    )
    .eq('event_type', 'click');

  if (destinationUrl === '(unknown link)') {
    query = query.or('destination_url.is.null,destination_url.eq.');
  } else {
    query = query.eq('destination_url', destinationUrl);
  }

  if ('newsletterId' in filter) {
    query = query.eq('scheduled_emails.newsletter_id', filter.newsletterId);
  } else {
    query = query.eq('scheduled_emails.automation_step_id', filter.automationStepId);
  }

  const { data, error } = await query.order('occurred_at', { ascending: false }).limit(5000);
  if (error) throw new Error(error.message);

  return aggregateLinkClickers((data ?? []) as unknown as LinkClickerEventRow[]);
}

/** Summary stats + click trend anchored at first send (or planned send). */
export async function loadNewsletterEngagementPanelData(
  newsletterId: string,
  recipientsSent: number,
  scheduledFor?: string | null,
): Promise<NewsletterEngagementPanelData> {
  const [stats, anchorAt, events, sends] = await Promise.all([
    loadNewsletterEngagement(newsletterId, recipientsSent),
    loadNewsletterEngagementAnchor(newsletterId, scheduledFor),
    loadNewsletterEngagementTrendEvents(newsletterId),
    loadNewsletterSendTimestamps(newsletterId),
  ]);

  const hasLiveTrend = stats.source === 'live' && !!anchorAt;

  return { stats, anchorAt, sends, events, hasLiveTrend };
}
